import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Collapse,
  Divider,
  InputAdornment,
  Chip
} from '@mui/material';
import { ExpandMore, ExpandLess, Refresh, FilterList, Clear, Search } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/apiErrorUtils';
import { formatPartMasterOptionLabel } from '../../utils/partMasterLabel';

const ReconciliationModule = ({ recordId: linkRecordId, recordType }) => {
  const [inventory, setInventory] = useState([]);
  const [formData, setFormData] = useState({
    reconciliationType: 'CYCLE_COUNT',
    findings: '',
    correctiveActions: ''
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reconciliationHistory, setReconciliationHistory] = useState([]);
  const [loadingReconciliationHistory, setLoadingReconciliationHistory] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const { showToast } = useToast();

  const fetchReconciliationHistory = useCallback(async () => {
    try {
      setLoadingReconciliationHistory(true);
      const response = await axios.get('/api/store/reconciliations');
      setReconciliationHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching reconciliation history:', error);
      showToast(getErrorMessage(error, 'Error loading reconciliation history'), 'error');
    } finally {
      setLoadingReconciliationHistory(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchInventory();
  }, []);

  useEffect(() => {
    fetchReconciliationHistory();
  }, [fetchReconciliationHistory]);

  useEffect(() => {
    if (recordType === 'reconciliation' && linkRecordId && reconciliationHistory.length > 0) {
      const exists = reconciliationHistory.some((r) => String(r._id) === String(linkRecordId));
      if (exists) setExpandedRows((prev) => ({ ...prev, [linkRecordId]: true }));
    }
  }, [recordType, linkRecordId, reconciliationHistory]);

  const fetchInventory = async () => {
    try {
      const response = await axios.get('/api/store/inventory');
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const handleAddItem = () => {
    setItems([...items, {
      inventoryItemId: '',
      partNumber: '',
      systemCount: 0,
      physicalCount: 0,
      discrepancy: 0,
      discrepancyReason: '',
      adjustedQuantity: 0
    }]);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Calculate discrepancy if counts are updated
    if (field === 'systemCount' || field === 'physicalCount') {
      const systemCount = field === 'systemCount' ? value : newItems[index].systemCount;
      const physicalCount = field === 'physicalCount' ? value : newItems[index].physicalCount;
      newItems[index].discrepancy = physicalCount - systemCount;
      newItems[index].adjustedQuantity = physicalCount;
    }

    // If inventoryItemId changed, update partNumber and systemCount
    if (field === 'inventoryItemId') {
      const item = inventory.find(i => i._id === value);
      if (item) {
        newItems[index].partNumber = item.partNumber;
        newItems[index].systemCount = item.quantity;
        newItems[index].physicalCount = item.quantity;
        newItems[index].discrepancy = 0;
        newItems[index].adjustedQuantity = item.quantity;
      }
    }

    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      showToast('Please add at least one item', 'error');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/store/reconcile', {
        ...formData,
        items
      });
      showToast('Reconciliation completed successfully', 'success');
      setFormData({ reconciliationType: 'CYCLE_COUNT', findings: '', correctiveActions: '' });
      setItems([]);
      fetchInventory();
      fetchReconciliationHistory();
    } catch (error) {
      console.error('Error reconciling inventory:', error);
      showToast(error.response?.data?.message || 'Error reconciling inventory', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (recordId) => {
    setExpandedRows((prev) => ({ ...prev, [recordId]: !prev[recordId] }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateFromFilter('');
    setDateToFilter('');
    setTypeFilter('ALL');
    setStatusFilter('ALL');
  };

  const hasActiveFilters = () =>
    searchTerm !== '' ||
    dateFromFilter !== '' ||
    dateToFilter !== '' ||
    typeFilter !== 'ALL' ||
    statusFilter !== 'ALL';

  const filterReconciliationRecords = (records) => {
    return records.filter((rec) => {
      if (typeFilter !== 'ALL' && rec.reconciliationType !== typeFilter) return false;
      if (statusFilter !== 'ALL' && rec.status !== statusFilter) return false;
      if (dateFromFilter) {
        const fromDate = new Date(dateFromFilter);
        fromDate.setHours(0, 0, 0, 0);
        const recDate = new Date(rec.reconciliationDate);
        if (recDate < fromDate) return false;
      }
      if (dateToFilter) {
        const toDate = new Date(dateToFilter);
        toDate.setHours(23, 59, 59, 999);
        const recDate = new Date(rec.reconciliationDate);
        if (recDate > toDate) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const partNumbers = (rec.items || []).map((i) => (i.partNumber || '').toLowerCase()).join(' ');
        const reconciledByName = `${rec.reconciledBy?.firstName || ''} ${rec.reconciledBy?.lastName || ''}`.toLowerCase();
        const findings = (rec.findings || '').toLowerCase();
        const correctiveActions = (rec.correctiveActions || '').toLowerCase();
        if (
          !partNumbers.includes(term) &&
          !reconciledByName.includes(term) &&
          !findings.includes(term) &&
          !correctiveActions.includes(term)
        )
          return false;
      }
      return true;
    });
  };

  const filteredReconciliation = filterReconciliationRecords(reconciliationHistory);

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Inventory Reconciliation
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Conduct cycle counts and full audits. Reconcile physical stock with system records.
      </Alert>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Reconciliation Type</InputLabel>
              <Select
                value={formData.reconciliationType}
                label="Reconciliation Type"
                onChange={(e) => setFormData({ ...formData, reconciliationType: e.target.value })}
              >
                <MenuItem value="CYCLE_COUNT">Cycle Count</MenuItem>
                <MenuItem value="FULL_AUDIT">Full Audit</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Items to Reconcile</Typography>
            <Button variant="outlined" onClick={handleAddItem}>
              Add Item
            </Button>
          </Box>

          {items.length === 0 ? (
            <Alert severity="info">Add items to reconcile</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>System Count</TableCell>
                    <TableCell>Physical Count</TableCell>
                    <TableCell>Discrepancy</TableCell>
                    <TableCell>Adjusted Qty</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={item.inventoryItemId}
                            onChange={(e) => handleItemChange(index, 'inventoryItemId', e.target.value)}
                          >
                            <MenuItem value="">Select Item</MenuItem>
                            {inventory.map((inv) => (
                              <MenuItem key={inv._id} value={inv._id}>
                                {formatPartMasterOptionLabel(inv)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>{item.systemCount}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.physicalCount}
                          onChange={(e) => handleItemChange(index, 'physicalCount', parseInt(e.target.value) || 0)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={item.discrepancy !== 0 ? 'error' : 'text.secondary'}
                        >
                          {item.discrepancy > 0 ? '+' : ''}{item.discrepancy}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.adjustedQuantity}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.discrepancyReason}
                          onChange={(e) => handleItemChange(index, 'discrepancyReason', e.target.value)}
                          placeholder="Reason for discrepancy"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Findings"
              value={formData.findings}
              onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Corrective Actions"
              value={formData.correctiveActions}
              onChange={(e) => setFormData({ ...formData, correctiveActions: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || items.length === 0}
          >
            {loading ? 'Reconciling...' : 'Complete Reconciliation'}
          </Button>
        </Box>
      </Paper>

      {/* Inventory Reconciliation History Section */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Inventory Reconciliation History</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchReconciliationHistory}
            disabled={loadingReconciliationHistory}
          >
            Refresh
          </Button>
        </Box>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Filters
              </Typography>
              {hasActiveFilters() && (
                <Chip
                  label={`${[searchTerm && 'Search', dateFromFilter && 'Date From', dateToFilter && 'Date To', typeFilter !== 'ALL' && 'Type', statusFilter !== 'ALL' && 'Status'].filter(Boolean).length} active`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={filtersExpanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                {filtersExpanded ? 'Hide' : 'Show'} Advanced
              </Button>
              {hasActiveFilters() && (
                <Button size="small" variant="outlined" color="secondary" startIcon={<Clear />} onClick={handleClearFilters}>
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by part number, findings, reconciled by..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="ALL">All Types</MenuItem>
                  <MenuItem value="CYCLE_COUNT">Cycle Count</MenuItem>
                  <MenuItem value="FULL_AUDIT">Full Audit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Collapse in={filtersExpanded} timeout="auto">
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date From"
                    type="date"
                    value={dateFromFilter}
                    onChange={(e) => setDateFromFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Date To"
                    type="date"
                    value={dateToFilter}
                    onChange={(e) => setDateToFilter(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Paper>

        {hasActiveFilters() && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredReconciliation.length} of {reconciliationHistory.length} reconciliation records
            </Typography>
          </Box>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px" />
                <TableCell>Reconciliation Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Reconciled By</TableCell>
                <TableCell>Findings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingReconciliationHistory ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Loading reconciliation history...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredReconciliation.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No reconciliation records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReconciliation.map((record) => (
                  <React.Fragment key={record._id}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleRowExpansion(record._id)}>
                          {expandedRows[record._id] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{new Date(record.reconciliationDate).toLocaleString()}</TableCell>
                      <TableCell>{record.reconciliationType === 'FULL_AUDIT' ? 'Full Audit' : 'Cycle Count'}</TableCell>
                      <TableCell>
                        <Chip label={record.status} color={getStatusColor(record.status)} size="small" />
                      </TableCell>
                      <TableCell>{(record.items || []).length} item(s)</TableCell>
                      <TableCell>
                        {record.reconciledBy
                          ? `${record.reconciledBy.firstName || ''} ${record.reconciledBy.lastName || ''}`.trim() || 'N/A'
                          : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {record.findings ? (
                          <Typography noWrap title={record.findings} variant="body2">
                            {record.findings}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0 }}>
                        <Collapse in={expandedRows[record._id]} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Reconciled Items
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Part Number</TableCell>
                                  <TableCell>System Count</TableCell>
                                  <TableCell>Physical Count</TableCell>
                                  <TableCell>Discrepancy</TableCell>
                                  <TableCell>Adjusted Qty</TableCell>
                                  <TableCell>Reason</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(record.items || []).map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.partNumber}</TableCell>
                                    <TableCell>{item.systemCount ?? '—'}</TableCell>
                                    <TableCell>{item.physicalCount ?? '—'}</TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        color={item.discrepancy !== 0 ? 'error' : 'text.secondary'}
                                      >
                                        {item.discrepancy != null ? (item.discrepancy > 0 ? '+' : '') + item.discrepancy : '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>{item.adjustedQuantity ?? '—'}</TableCell>
                                    <TableCell>{item.discrepancyReason || '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            {record.findings && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Findings
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  {record.findings}
                                </Typography>
                              </>
                            )}
                            {record.correctiveActions && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Corrective Actions
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  {record.correctiveActions}
                                </Typography>
                              </>
                            )}
                            {(record.statusHistory || []).length > 0 && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                  Status History
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                  {record.statusHistory.map((entry, idx) => (
                                    <Typography key={idx} component="li" variant="body2" color="text.secondary">
                                      {entry.status}
                                      {entry.notes ? ` — ${entry.notes}` : ''} (
                                      {entry.changedAt ? new Date(entry.changedAt).toLocaleString() : 'N/A'})
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ReconciliationModule;

